/**
 * Product Dev — list page.
 *
 * Inventory-app surface (AppLayout). Access gated by the `product_dev.view`
 * RBAC permission at the sidebar entry + route RoleGuard. Same pattern as
 * Purchase Orders / Inventory — no env-var feature flag.
 */
import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronDown, Filter, FilterX, Plus, Loader2, Search, Shirt, ShoppingCart, SquarePen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { toast } from '@/hooks/use-toast';
import { useRole } from '@/hooks/use-role';
import { useSuppliers } from '@/features/suppliers/hooks/useSuppliers';
import {
  useProductDevList,
  useProductDevMutations,
  useProductDevGroupingValues,
  useMentionableUsers,
} from '@/features/product-dev/hooks/useProductDev';
import {
  PRODUCT_DEV_STAGES,
  PRODUCT_DEV_TASK_STAGES,
  PRODUCT_DEV_STATUSES,
  PRODUCT_TYPES,
  RECORD_TYPES,
  STAGE_GROUP_ORDER,
  STAGE_PILL_CLASS,
  STATUS_GROUP_ORDER,
  STATUS_PILL_CLASS,
  priorityRank,
  type ProductDevPriority,
  type ProductDevRecordType,
  type ProductType,
} from '@/features/product-dev/utils/status-style';
import { PriorityFlag } from '@/features/product-dev/components/PriorityFlag';
import { BatchActionBar } from '@/features/product-dev/components/BatchActionBar';
import {
  CapsuleOrderDialog,
  type CapsuleRecord,
} from '@/features/product-dev/components/CapsuleOrderDialog';
import { toRelativeDate, isOverdue } from '@/features/product-dev/utils/date-format';
import { cn } from '@/lib/utils';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { PageActionBar, PageActionBarDivider } from '@/components/layout/PageActionBar';
import { BackToTopPill } from '@/components/BackToTopPill';
import { PageLoader } from '@/components/loading/PageLoader';

type RecordTypeChip = 'all' | ProductDevRecordType;

type GroupBy = 'status' | 'stage' | 'brand' | 'capsule' | 'none';

const GROUP_BY_STORAGE_KEY = 'productDev.groupBy';
const MAX_BATCH_SELECTION = 50;

/** Bucket key for rows with no brand/capsule value. Sorted last, labelled via
 *  i18n (the real brand/capsule labels are raw user data and never translated). */
const UNASSIGNED_GROUP_KEY = '__unassigned__';

/** Workflow pill (stage / status / group header). Shared so the three pill
 *  sites stay geometrically identical — colour tokens are appended per state.
 *  The subtle inset ring gives the tinted pills a crisp edge without adding a
 *  new accent (DESIGN.md §7 pill discipline). */
const PILL_BASE =
  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ring-black/5';

/** Column-header micro-label — deliberate table header, not a browser default
 *  (mirrors the uppercase tracking-wide label convention in DESIGN.md §13.4). */
const TH_CLASS =
  'text-[11px] font-medium uppercase tracking-wide text-muted-foreground';

const isGroupBy = (v: string | null): v is GroupBy =>
  v === 'status' ||
  v === 'stage' ||
  v === 'brand' ||
  v === 'capsule' ||
  v === 'none';

/** Leading garment thumbnail for a list row. Real hero image when the record
 *  has one, otherwise a tidy muted tile with a garment glyph — never a borrowed
 *  photo, so the column reads consistently for image-less rows (tasks always
 *  fall here). ~40px, lazy-loaded; alt carries the style name for a11y. */
const RowThumbnail = ({ src, alt }: { src: string | null; alt: string }) => {
  if (!src) {
    return (
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-muted"
        aria-hidden="true"
      >
        <Shirt className="h-4 w-4 text-muted-foreground/70" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      className="h-10 w-10 shrink-0 rounded-md object-cover"
    />
  );
};

/** Tiny neutral tag for a row's grouping value (brand / capsule). Deliberately
 *  quiet — muted fill, no accent, no ring — so it sits under the created-at line
 *  without competing with the thumbnail or the style name. Only rendered when a
 *  value is set. `title` carries the dimension for hover/a11y context. */
const MetaChip = ({ label, title }: { label: string; title: string }) => (
  <span
    title={`${title}: ${label}`}
    aria-label={`${title}: ${label}`}
    tabIndex={0}
    className="inline-flex max-w-[32ch] items-center truncate rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium leading-none text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  >
    {label}
  </span>
);

const ProductTypeBadge = ({ type }: { type: string }) => {
  const { t } = useTranslation('productDev');
  return (
    <span className="text-xs text-muted-foreground">
      {type ? t(`productType.${type}`) : type}
    </span>
  );
};

export default function ProductDev() {
  const { t } = useTranslation('productDev');
  const navigate = useNavigate();
  usePageTitle(t('list.title'), t('list.subtitle'));
  const [recordTypeChip, setRecordTypeChip] = useState<RecordTypeChip>('all');
  const [stageFilter, setStageFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [capsuleFilter, setCapsuleFilter] = useState<string>('all');
  const [supplierFilter, setSupplierFilter] = useState<string>('all');
  const [procurementFilter, setProcurementFilter] = useState<'all' | 'in_dev' | 'moved'>('all');
  const [search, setSearch] = useState('');
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    const stored = localStorage.getItem(GROUP_BY_STORAGE_KEY);
    return isGroupBy(stored) ? stored : 'status';
  });
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [batchEditorOpen, setBatchEditorOpen] = useState(false);
  const { can } = useRole();
  const canEdit = can('product_dev.manage');
  const { suppliers } = useSuppliers();
  const groupingValues = useProductDevGroupingValues();
  const brandOptions = groupingValues.data?.brands ?? [];
  const capsuleOptions = groupingValues.data?.capsules ?? [];

  // Assignee options for the batch bar (org members), same source the detail
  // page uses for its "Assigned to" picker.
  const membersQuery = useMentionableUsers('product_dev', canEdit);
  const members = (membersQuery.data ?? []).map((u) => ({ id: u.id, label: u.display_name }));

  // Batch-select state. Cleared whenever the visible set changes (any filter,
  // the search term, or the grouping dimension) so a stale selection can never
  // act on rows the operator can no longer see.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelected(new Set());
    setBatchEditorOpen(false);
  }, [stageFilter, statusFilter, typeFilter, brandFilter, capsuleFilter, supplierFilter, procurementFilter, recordTypeChip, search, groupBy]);

  const selectionLimitToast = () =>
    toast({
      title: t('list.batch.limitTitle', { count: MAX_BATCH_SELECTION }),
      description: t('list.batch.limitBody'),
    });

  const toggleRow = (id: string) => {
    if (!selected.has(id) && selected.size >= MAX_BATCH_SELECTION) {
      selectionLimitToast();
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setRowsSelected = (ids: string[], select: boolean) => {
    if (select) {
      const additions = ids.filter((id) => !selected.has(id));
      const room = Math.max(0, MAX_BATCH_SELECTION - selected.size);
      if (additions.length > room) selectionLimitToast();
      const accepted = additions.slice(0, room);
      setSelected((prev) => new Set([...prev, ...accepted]));
      return;
    }
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        next.delete(id);
      }
      return next;
    });
  };

  const editGroup = (groupIds: string[]) => {
    if (groupIds.length > MAX_BATCH_SELECTION) {
      selectionLimitToast();
      return;
    }
    setSelected(new Set(groupIds));
    setBatchEditorOpen(true);
  };

  const changeGroupBy = (v: GroupBy) => {
    setGroupBy(v);
    localStorage.setItem(GROUP_BY_STORAGE_KEY, v);
    setCollapsedGroups(new Set());
  };

  const changeRecordTypeChip = (v: RecordTypeChip) => {
    setRecordTypeChip(v);
    // Stage chains differ per record type — reset a now-invalid stage filter.
    setStageFilter('all');
  };

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Record type is filtered client-side (below) so the chip row can show live
  // per-type counts from a single query. Stage/status/type stay server-side.
  const query = useProductDevList({
    stage: stageFilter === 'all' ? null : stageFilter,
    status: statusFilter === 'all' ? null : statusFilter,
    productType: typeFilter === 'all' ? null : (typeFilter as ProductType),
    supplierId: supplierFilter === 'all' ? null : supplierFilter,
  });

  const [createOpen, setCreateOpen] = useState(false);
  const [capsuleOrderOpen, setCapsuleOrderOpen] = useState(false);
  const [newRecordType, setNewRecordType] = useState<ProductDevRecordType>('new_product');
  const [newStyleName, setNewStyleName] = useState('');
  const [newType, setNewType] = useState<ProductType>('finished_garment');
  const [newSupplierId, setNewSupplierId] = useState<string>('none');
  const { create, update } = useProductDevMutations();

  // Single-row triage flag. Returns the mutation promise so PriorityFlag can
  // revert its optimistic paint on failure; toast so the failure isn't silent.
  const cyclePriority = async (id: string, next: ProductDevPriority | null) => {
    try {
      await update.mutateAsync({ productDevId: id, updates: { priority: next } });
    } catch (err) {
      toast({
        title: t('priority.saveFailed'),
        description: err instanceof Error ? err.message : t('list.toast.tryAgain'),
        variant: 'destructive',
      });
      throw err;
    }
  };

  const resetCreateForm = () => {
    setNewRecordType('new_product');
    setNewStyleName('');
    setNewType('finished_garment');
    setNewSupplierId('none');
  };

  const handleCreate = async () => {
    const styleName = newStyleName.trim();
    if (!styleName) return;
    try {
      const created = await create.mutateAsync({
        style_name: styleName,
        record_type: newRecordType,
        // Tasks have no product type — the backend rejects one anyway.
        product_type: newRecordType === 'task' ? null : newType,
        supplier_id:
          newRecordType === 'task' && newSupplierId !== 'none' ? newSupplierId : undefined,
      });
      setCreateOpen(false);
      resetCreateForm();
      navigate(`/product-dev/${created.id}`);
    } catch (err) {
      toast({
        title: t('list.toast.createFailedTitle'),
        description: err instanceof Error ? err.message : t('list.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const now = new Date();
  const allRows = query.data ?? [];
  const term = search.trim().toLowerCase();
  // Brand + capsule filter client-side (no server param) alongside search —
  // both applied before the record-type chip so chip counts reflect them.
  const filteredRows = allRows.filter(
    (r) =>
      (brandFilter === 'all' || r.brand === brandFilter) &&
      (capsuleFilter === 'all' || r.capsule === capsuleFilter) &&
      (procurementFilter === 'all' ||
        (procurementFilter === 'moved' ? Boolean(r.linked_purchase_order_id) : !r.linked_purchase_order_id)),
  );
  const searchRows = term
    ? filteredRows.filter((r) =>
        r.style_name.toLowerCase().includes(term) ||
        (r.style_code ?? '').toLowerCase().includes(term) ||
        (r.supplier_title ?? '').toLowerCase().includes(term) ||
        (r.brand ?? '').toLowerCase().includes(term) ||
        (r.capsule ?? '').toLowerCase().includes(term),
      )
    : filteredRows;

  // Live chip counts come from the search-filtered set BEFORE the record-type
  // filter, so each chip shows how many rows match the OTHER filters.
  const chipCounts: Record<RecordTypeChip, number> = {
    all: searchRows.length,
    new_product: 0,
    product_change: 0,
    task: 0,
  };
  for (const r of searchRows) {
    if (r.record_type in chipCounts) chipCounts[r.record_type as RecordTypeChip] += 1;
  }

  const visibleRows =
    recordTypeChip === 'all'
      ? searchRows
      : searchRows.filter((r) => r.record_type === recordTypeChip);

  // Chip-tab definitions. Tasks is conditional: it only appears while legacy
  // task records remain in the filtered set (tasks can no longer be created),
  // so the tab, and with it the concept, disappears once that data is cleared.
  const recordTabs: { value: RecordTypeChip; label: string; count: number }[] = [
    { value: 'all', label: t('list.chip.all'), count: chipCounts.all },
    { value: 'new_product', label: t('list.chip.new_product'), count: chipCounts.new_product },
    { value: 'product_change', label: t('list.chip.product_change'), count: chipCounts.product_change },
  ];
  if (chipCounts.task > 0) {
    recordTabs.push({ value: 'task', label: t('list.chip.task'), count: chipCounts.task });
  }

  // If the Tasks tab drops out (its records fell out of the filtered set) while
  // it's the active tab, fall back to All so the operator isn't stranded on a
  // now-hidden, empty tab. Use the full change handler (not the raw setter) so
  // any task-specific stage filter is cleared too — otherwise the operator
  // lands on All with an invisible task-only stage filter still applied.
  useEffect(() => {
    if (recordTypeChip === 'task' && chipCounts.task === 0) changeRecordTypeChip('all');
  }, [recordTypeChip, chipCounts.task]);

  // The six dropdown filters collapsed into the "Filters" popover — their
  // active count drives the trigger badge so a narrowed list is legible even
  // when the controls themselves are hidden behind the popover.
  const popoverFilterCount =
    (stageFilter !== 'all' ? 1 : 0) +
    (statusFilter !== 'all' ? 1 : 0) +
    (typeFilter !== 'all' ? 1 : 0) +
    (brandFilter !== 'all' ? 1 : 0) +
    (capsuleFilter !== 'all' ? 1 : 0) +
    (supplierFilter !== 'all' ? 1 : 0) +
    (procurementFilter !== 'all' ? 1 : 0);

  const hasActiveFilters =
    popoverFilterCount > 0 ||
    recordTypeChip !== 'all' ||
    term !== '';

  const clearFilters = () => {
    setStageFilter('all');
    setStatusFilter('all');
    setTypeFilter('all');
    setBrandFilter('all');
    setCapsuleFilter('all');
    setSupplierFilter('all');
    setProcurementFilter('all');
    setRecordTypeChip('all');
    setSearch('');
  };

  // Clear just the popover's six dropdowns (leaves search + record-type chip
  // untouched) — the "Clear filters" action inside the popover footer.
  const clearPopoverFilters = () => {
    setStageFilter('all');
    setStatusFilter('all');
    setTypeFilter('all');
    setBrandFilter('all');
    setCapsuleFilter('all');
    setSupplierFilter('all');
    setProcurementFilter('all');
  };

  // The active group-by dimension is shown in every group header, so its
  // per-row column is redundant — hide it to cut noise (columns stay aligned
  // because the header + every row drop it together). Brand/capsule are NOT
  // table columns, so grouping by them hides nothing: Priority + both stage +
  // status columns stay (colCount = selectCol + 8), and the header split
  // (colCount - 1 + the Expected cell) still lands the overdue rollup under
  // the Expected column.
  const hideStageCol = groupBy === 'stage';
  const hideStatusCol = groupBy === 'status';
  // +1 leading column for the row-select checkbox when the operator can edit.
  const selectCol = canEdit ? 1 : 0;
  // Fixed body columns: Style, Priority, Type, Supplier, Procurement, Assigned, Expected (7).
  // Stage + Status are each +1 when not hidden by the active group-by.
  const colCount = selectCol + 7 + (hideStageCol ? 0 : 1) + (hideStatusCol ? 0 : 1);

  // Grouping. Two flavours share one machine:
  //  - Workflow (status/stage): known keys in curated worklist order, unknown
  //    keys appended (forward-compat), coloured pill per state.
  //  - Data (brand/capsule): raw user values, alphabetical (case-insensitive),
  //    null/blank collapsed into one "Unassigned" group sorted last, neutral
  //    pill (no semantic colour — these aren't states).
  // Empty groups are hidden in both.
  const groups = (() => {
    if (groupBy === 'none') return null;
    const isWorkflow = groupBy === 'status' || groupBy === 'stage';
    const byKey = new Map<string, typeof visibleRows>();
    for (const row of visibleRows) {
      const raw =
        groupBy === 'status'
          ? row.status
          : groupBy === 'stage'
            ? row.stage
            : groupBy === 'brand'
              ? row.brand
              : row.capsule;
      const key = isWorkflow
        ? (raw ?? 'unknown')
        : raw && raw.trim() !== ''
          ? raw
          : UNASSIGNED_GROUP_KEY;
      const bucket = byKey.get(key);
      if (bucket) bucket.push(row);
      else byKey.set(key, [row]);
    }

    let keys: string[];
    if (isWorkflow) {
      const order = groupBy === 'status' ? STATUS_GROUP_ORDER : STAGE_GROUP_ORDER;
      keys = [
        ...order.filter((k) => byKey.has(k)),
        ...[...byKey.keys()].filter((k) => !order.includes(k)),
      ];
    } else {
      keys = [...byKey.keys()]
        .filter((k) => k !== UNASSIGNED_GROUP_KEY)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
      if (byKey.has(UNASSIGNED_GROUP_KEY)) keys.push(UNASSIGNED_GROUP_KEY);
    }

    return keys.map((key) => {
      // Secondary priority sort inside every group: urgent, then high, then the
      // existing (server) order — stable sort preserves it for equal ranks.
      const groupRows = [...(byKey.get(key) ?? [])].sort(
        (a, b) => priorityRank(a.priority) - priorityRank(b.priority),
      );
      const isUnassigned = !isWorkflow && key === UNASSIGNED_GROUP_KEY;
      const label = isWorkflow
        ? t(`${groupBy === 'status' ? 'status' : 'stage'}.${key}`)
        : isUnassigned
          ? t('list.group.unassigned')
          : key;
      const pillClass = isWorkflow
        ? ((groupBy === 'status' ? STATUS_PILL_CLASS[key] : STAGE_PILL_CLASS[key]) ??
          'bg-muted text-muted-foreground')
        : 'bg-muted text-muted-foreground';
      return {
        key,
        label,
        pillClass,
        rows: groupRows,
        overdueCount: groupRows.filter((r) =>
          isOverdue(r.expected_completion_date, r.status, now),
        ).length,
      };
    });
  })();

  // Ungrouped view gets the same secondary priority sort as the grouped path.
  const ungroupedRows = [...visibleRows].sort(
    (a, b) => priorityRank(a.priority) - priorityRank(b.priority),
  );

  // Capsule-order flow: every record in the currently-selected capsule (all
  // record types, ignoring the record-type chip + search so the order covers
  // the whole capsule). Built from the loaded list, so any active server-side
  // stage/status filter narrows the set too. The Order-capsule button appears
  // once a specific capsule is selected and ≥1 record belongs to it; the
  // buy-plan requirement (skip records with no planned quantities) is enforced
  // inside the dialog, which resolves each record's variants.
  const capsuleRecords: CapsuleRecord[] =
    capsuleFilter === 'all'
      ? []
      : allRows
          .filter((r) => r.capsule === capsuleFilter)
          .map((r) => ({
            id: r.id,
            style_name: r.style_name,
            style_code: r.style_code,
            supplier_id: r.supplier_id,
            supplier_title: r.supplier_title,
            agreed_price: r.agreed_price,
            agreed_currency: r.agreed_currency,
          }));
  const canOrderCapsule = canEdit && capsuleFilter !== 'all' && capsuleRecords.length > 0;

  // Header (all-rows) select state — spans every visible row regardless of
  // grouping; each group header carries its own group-scoped toggle too.
  const visibleIds = visibleRows.map((r) => r.id);
  const selectedVisibleCount = visibleIds.filter((id) => selected.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;

  const renderExpected = (iso: string | null, status: string) => {
    if (!iso) return <span className="text-muted-foreground">-</span>;
    const rel = toRelativeDate(iso, now);
    const overdue = isOverdue(iso, status, now);
    return (
      <span
        title={rel.absolute}
        className={cn(
          'inline-flex items-center gap-1 text-sm tabular-nums',
          overdue ? 'font-medium text-rose-700' : 'text-muted-foreground',
        )}
      >
        {overdue && <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />}
        {rel.short}
        {overdue && <span className="sr-only"> {t('list.overdue')}</span>}
      </span>
    );
  };

  const renderRow = (row: (typeof visibleRows)[number]) => {
    const created = toRelativeDate(row.created_at, now);
    return (
      <TableRow
        key={row.id}
        className="group cursor-pointer hover:bg-muted/40 motion-reduce:hover:bg-muted/40 [&>td]:py-2.5"
        onClick={() => navigate(`/product-dev/${row.id}`)}
      >
        {canEdit && (
          <TableCell
            className="w-9 pr-0 align-middle"
            onClick={(e) => e.stopPropagation()}
          >
            <Checkbox
              checked={selected.has(row.id)}
              onCheckedChange={() => toggleRow(row.id)}
              aria-label={t('list.selectRowAria', { name: row.style_name })}
            />
          </TableCell>
        )}
        <TableCell>
          <div className="flex items-center gap-3">
            <RowThumbnail src={row.hero_image_url} alt={row.style_name} />
            <div className="min-w-0 flex flex-col gap-0.5">
              <div className="flex items-center gap-1.5">
                <Link
                  to={`/product-dev/${row.id}`}
                  className="font-medium hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.style_name}
                </Link>
              </div>
              <span
                className="text-xs text-muted-foreground tabular-nums"
                title={created.absolute}
              >
                {t('list.createdRelative', { when: created.short })}
              </span>
              <div className="flex flex-wrap items-center gap-1 pt-0.5">
                {/* Record-type provenance on every row — muted + tiny so it
                    reads as a label, not a chip competing with brand/capsule. */}
                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground/70">
                  {t(`recordType.${row.record_type}`)}
                </span>
                {row.brand && groupBy !== 'brand' && (
                  <MetaChip label={row.brand} title={t('list.col.brand')} />
                )}
                {row.capsule && groupBy !== 'capsule' && (
                  <MetaChip label={row.capsule} title={t('list.col.capsule')} />
                )}
              </div>
            </div>
          </div>
        </TableCell>
        {/* Priority — its own column (moved out of the Style cell). Empty-state
            flag stays hidden until row hover via revealOnHover, so the column
            reads clean. stopPropagation guards against the row navigation. */}
        <TableCell
          className="align-middle"
          onClick={(e) => e.stopPropagation()}
        >
          <PriorityFlag
            priority={row.priority}
            canEdit={canEdit}
            onCycle={(next) => cyclePriority(row.id, next)}
            revealOnHover
          />
        </TableCell>
        <TableCell>
          {row.record_type === 'task' ? (
            <span className="text-xs text-muted-foreground">
              {t('recordType.task')}
            </span>
          ) : (
            <ProductTypeBadge type={row.product_type ?? ''} />
          )}
        </TableCell>
        <TableCell className="text-sm">
          {row.supplier_title ?? <span className="text-muted-foreground">-</span>}
        </TableCell>
        <TableCell>
          <span className={cn(
            PILL_BASE,
            row.linked_purchase_order_id
              ? 'bg-emerald-50 text-emerald-700'
              : 'bg-slate-100 text-slate-600',
          )}>
            {t(row.linked_purchase_order_id ? 'procurement.moved' : 'procurement.inDev')}
          </span>
        </TableCell>
        {!hideStageCol && (
          <TableCell>
            <span className={cn(
              PILL_BASE,
              STAGE_PILL_CLASS[row.stage] ?? 'bg-muted text-muted-foreground',
            )}>
              {t(`stage.${row.stage}`, { defaultValue: row.stage })}
            </span>
          </TableCell>
        )}
        {!hideStatusCol && (
          <TableCell>
            <span className={cn(
              PILL_BASE,
              STATUS_PILL_CLASS[row.status] ?? 'bg-muted text-muted-foreground',
            )}>
              {t(`status.${row.status}`, { defaultValue: row.status })}
            </span>
          </TableCell>
        )}
        <TableCell className="text-sm">
          {row.assigned_to_name ?? <span className="text-muted-foreground">-</span>}
        </TableCell>
        <TableCell>{renderExpected(row.expected_completion_date, row.status)}</TableCell>
      </TableRow>
    );
  };

  return (
    <div className="w-full py-6 px-4 sm:px-6 lg:px-8">
      {/* Sticky filter chrome (DESIGN.md §3). Only search + group-by stay inline
          in the bar's left — the six dropdown filters (stage/status/type/brand/
          capsule/supplier) collapse into a "Filters" popover so the cluster
          stays one row on narrow screens (owner feedback 2026-07-16: the inline
          run of selects ate too much width). Mirrors the advanced-filter popover
          on the Inventory list (InventoryTableFilters). Group-by stays visible
          and first per DESIGN.md §14.1. Order capsule + New record own the right;
          the record-type tabs lead the table in-body. */}
      <PageActionBar
        ariaLabel={t('list.title')}
        left={
          <>
            <div className="relative w-44 sm:w-56">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('list.search')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 pl-9 text-xs"
              />
            </div>
            <PageActionBarDivider />
            {/* Group-by sits first in the filter cluster (DESIGN.md §14.1). */}
            <Select value={groupBy} onValueChange={(v) => changeGroupBy(v as GroupBy)}>
              <SelectTrigger className="h-8 w-[140px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="status">{t('list.groupBy.status')}</SelectItem>
                <SelectItem value="stage">{t('list.groupBy.stage')}</SelectItem>
                <SelectItem value="brand">{t('list.groupBy.brand')}</SelectItem>
                <SelectItem value="capsule">{t('list.groupBy.capsule')}</SelectItem>
                <SelectItem value="none">{t('list.groupBy.none')}</SelectItem>
              </SelectContent>
            </Select>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={cn(
                    'h-8 gap-1.5 px-3 text-xs',
                    popoverFilterCount > 0 &&
                      'border-threadheads-purple/40 text-threadheads-purple hover:text-threadheads-purple',
                  )}
                >
                  {popoverFilterCount > 0 ? (
                    <FilterX className="h-3.5 w-3.5" />
                  ) : (
                    <Filter className="h-3.5 w-3.5" />
                  )}
                  {t('list.filters')}
                  {popoverFilterCount > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-0.5 h-4 min-w-4 justify-center rounded-full bg-threadheads-purple px-1 text-[10px] font-semibold text-white"
                    >
                      {popoverFilterCount}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-64 p-4">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t('list.filterLabel.stage')}</Label>
                    <Select value={stageFilter} onValueChange={setStageFilter}>
                      <SelectTrigger className="h-8 w-full text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('list.filter.allStages')}</SelectItem>
                        {(recordTypeChip === 'task'
                          ? PRODUCT_DEV_TASK_STAGES
                          : recordTypeChip === 'all'
                            ? [...PRODUCT_DEV_STAGES, ...PRODUCT_DEV_TASK_STAGES]
                            : PRODUCT_DEV_STAGES
                        ).map((s) => (
                          <SelectItem key={s} value={s}>{t(`stage.${s}`, { defaultValue: s })}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t('list.filterLabel.status')}</Label>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="h-8 w-full text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('list.filter.allStatuses')}</SelectItem>
                        {PRODUCT_DEV_STATUSES.map((s) => (
                          <SelectItem key={s} value={s}>{t(`status.${s}`, { defaultValue: s })}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t('list.filterLabel.type')}</Label>
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                      <SelectTrigger className="h-8 w-full text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('list.filter.allTypes')}</SelectItem>
                        {PRODUCT_TYPES.map((pt) => (
                          <SelectItem key={pt} value={pt}>{t(`productType.${pt}`)}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t('list.filterLabel.supplier')}</Label>
                    <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                      <SelectTrigger className="h-8 w-full text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('list.filter.allSuppliers')}</SelectItem>
                        {suppliers.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-muted-foreground">{t('list.filterLabel.procurement')}</Label>
                    <Select value={procurementFilter} onValueChange={(value) => setProcurementFilter(value as typeof procurementFilter)}>
                      <SelectTrigger className="h-8 w-full text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('procurement.all')}</SelectItem>
                        <SelectItem value="in_dev">{t('procurement.inDev')}</SelectItem>
                        <SelectItem value="moved">{t('procurement.moved')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {brandOptions.length > 0 && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">{t('list.filterLabel.brand')}</Label>
                      <Select value={brandFilter} onValueChange={setBrandFilter}>
                        <SelectTrigger className="h-8 w-full text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('list.filter.allBrands')}</SelectItem>
                          {brandOptions.map((b) => (
                            <SelectItem key={b} value={b}>{b}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {capsuleOptions.length > 0 && (
                    <div className="space-y-1.5">
                      <Label className="text-xs text-muted-foreground">{t('list.filterLabel.capsule')}</Label>
                      <Select value={capsuleFilter} onValueChange={setCapsuleFilter}>
                        <SelectTrigger className="h-8 w-full text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">{t('list.filter.allCapsules')}</SelectItem>
                          {capsuleOptions.map((c) => (
                            <SelectItem key={c} value={c}>{c}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex justify-end border-t pt-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={clearPopoverFilters}
                      disabled={popoverFilterCount === 0}
                    >
                      {t('list.clearFilters')}
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </>
        }
        right={
          <>
            {canOrderCapsule && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCapsuleOrderOpen(true)}
                className="h-8 gap-1.5 border-threadheads-purple/40 px-3 text-xs text-threadheads-purple hover:bg-threadheads-purple/5 hover:text-threadheads-purple"
              >
                <ShoppingCart className="h-3.5 w-3.5" /> {t('capsuleOrder.orderCapsule')}
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => setCreateOpen(true)}
              className="h-8 gap-1.5 px-3 text-xs"
            >
              <Plus className="h-3.5 w-3.5" /> {t('list.new')}
            </Button>
          </>
        }
      />

      {/* Record-type tabs — the primary "which records am I looking at" switch.
          [All | New products | Changes] always; Tasks appears only while legacy
          task records still exist (tasks can no longer be created), so the
          concept quietly retires once that data is cleared. Live counts mirror
          the other filters. */}
      <Tabs
        value={recordTypeChip}
        onValueChange={(v) => changeRecordTypeChip(v as RecordTypeChip)}
        className="mt-6"
      >
        <TabsList className="h-9 max-w-full justify-start overflow-x-auto">
          {recordTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="shrink-0 gap-1.5 text-xs">
              {tab.label}
              <span className="tabular-nums text-muted-foreground">{tab.count}</span>
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Order the whole selected capsule into one purchase order. */}
      {capsuleFilter !== 'all' && (
        <CapsuleOrderDialog
          open={capsuleOrderOpen}
          onOpenChange={setCapsuleOrderOpen}
          capsule={capsuleFilter}
          records={capsuleRecords}
        />
      )}

      {/* Table */}
      <Card className="mt-6">
        <CardContent className="p-0">
          {query.isLoading ? (
            <PageLoader pool="productDev" />
          ) : query.error ? (
            <div className="p-6 text-sm text-destructive">
              {t('list.loadFailed', { message: query.error.message })}
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="p-10 text-center">
              <p className="text-sm text-muted-foreground">
                {hasActiveFilters ? t('list.emptyFiltered') : t('list.emptyNone')}
              </p>
              {hasActiveFilters ? (
                <Button variant="outline" size="sm" onClick={clearFilters} className="mt-4">
                  {t('list.clearFilters')}
                </Button>
              ) : (
                <Button onClick={() => setCreateOpen(true)} className="mt-4">
                  <Plus className="h-4 w-4 mr-2" /> {t('list.createFirst')}
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  {canEdit && (
                    <TableHead className="w-9 pr-0">
                      <Checkbox
                        checked={
                          someVisibleSelected ? 'indeterminate' : allVisibleSelected
                        }
                        onCheckedChange={() =>
                          setRowsSelected(visibleIds, selectedVisibleCount === 0)
                        }
                        aria-label={t('list.selectAllAria')}
                      />
                    </TableHead>
                  )}
                  <TableHead className={TH_CLASS}>{t('list.col.style')}</TableHead>
                  <TableHead className={cn(TH_CLASS, 'w-[84px]')}>{t('list.col.priority')}</TableHead>
                  <TableHead className={TH_CLASS}>{t('list.col.type')}</TableHead>
                  <TableHead className={TH_CLASS}>{t('list.col.supplier')}</TableHead>
                  <TableHead className={TH_CLASS}>{t('list.col.procurement')}</TableHead>
                  {!hideStageCol && <TableHead className={TH_CLASS}>{t('list.col.stage')}</TableHead>}
                  {!hideStatusCol && <TableHead className={TH_CLASS}>{t('list.col.status')}</TableHead>}
                  <TableHead className={TH_CLASS}>{t('list.col.assigned')}</TableHead>
                  <TableHead className={TH_CLASS}>{t('list.col.expected')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {groups ? (
                  groups.map((group) => {
                    const collapsed = collapsedGroups.has(group.key);
                    const groupIds = group.rows.map((r) => r.id);
                    const groupSelectedCount = groupIds.filter((id) => selected.has(id)).length;
                    const groupAllSelected =
                      groupIds.length > 0 && groupSelectedCount === groupIds.length;
                    const groupSomeSelected = groupSelectedCount > 0 && !groupAllSelected;
                    return (
                      <Fragment key={group.key}>
                        <TableRow
                          className="cursor-pointer border-b bg-muted/40 hover:bg-muted/60 [&>td]:py-2"
                          onClick={() => toggleGroup(group.key)}
                        >
                          {/* Left: a group-scoped select-all checkbox + the
                              toggle control span every column up to Expected, so
                              the overdue badge can sit in the Expected column
                              itself (aligned with the per-row "Nmo ago" values
                              below). The checkbox lives INSIDE this colSpan cell
                              (before the chevron) with its own stopPropagation so
                              it never fires the row's collapse toggle. colCount is
                              live, so the split stays correct when stage/status
                              cols hide. */}
                          <TableCell colSpan={colCount - 1} className="align-middle">
                            <div className="flex items-center gap-2">
                              {canEdit && (
                                <span
                                  className="inline-flex"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Checkbox
                                    checked={
                                      groupSomeSelected ? 'indeterminate' : groupAllSelected
                                    }
                                    onCheckedChange={() =>
                                      setRowsSelected(groupIds, groupSelectedCount === 0)
                                    }
                                    aria-label={t('list.selectGroupAria', {
                                      group: group.label,
                                    })}
                                  />
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleGroup(group.key);
                                }}
                                aria-expanded={!collapsed}
                                className="flex items-center gap-2 rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              >
                                <ChevronDown
                                  aria-hidden="true"
                                  className={cn(
                                    'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 motion-reduce:transition-none',
                                    collapsed && '-rotate-90',
                                  )}
                                />
                                <span className={cn(PILL_BASE, group.pillClass)}>
                                  {group.label}
                                </span>
                                <span className="text-xs text-muted-foreground/80 tabular-nums">
                                  {group.rows.length}
                                </span>
                              </button>
                              {canEdit && (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="ml-auto h-7 shrink-0 gap-1.5 px-2 text-xs text-muted-foreground hover:text-threadheads-purple"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    editGroup(groupIds);
                                  }}
                                  aria-label={t('list.batch.updateGroupAria', { group: group.label })}
                                >
                                  <SquarePen className="h-3.5 w-3.5" />
                                  {t('list.batch.updateGroup')}
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          {/* Right: the Expected column. A quiet rose tally, NOT
                              a second alarm — the per-row AlertTriangle values
                              below own the alarm. No icon, lighter weight/size so
                              it reads as a count while staying aligned under the
                              Expected column. */}
                          <TableCell className="align-middle">
                            {group.overdueCount > 0 && (
                              <span className="text-xs text-rose-600 tabular-nums">
                                {t('list.overdueCount', { count: group.overdueCount })}
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                        {!collapsed && group.rows.map(renderRow)}
                      </Fragment>
                    );
                  })
                ) : (
                  ungroupedRows.map(renderRow)
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Floating bulk-action bar — only while a selection exists. */}
      {canEdit && selected.size > 0 && (
        <BatchActionBar
          selectedIds={[...selected]}
          selectedProducts={visibleRows
            .filter((row) => selected.has(row.id))
            .map((row) => ({
              id: row.id,
              styleName: row.style_name,
              styleCode: row.style_code,
              supplierId: row.supplier_id,
              supplierTitle: row.supplier_title,
              agreedPrice: row.agreed_price,
              agreedCurrency: row.agreed_currency,
              proposedQuantity: row.proposed_quantity,
              moq: row.moq,
            }))}
          members={members}
          onDone={() => {
            setBatchEditorOpen(false);
            setSelected(new Set());
          }}
          editorOpen={batchEditorOpen}
          onEditorOpenChange={setBatchEditorOpen}
        />
      )}

      {/* Create dialog — record type first, then adaptive fields */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) resetCreateForm();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('list.dialog.title')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>{t('list.dialog.recordType')}</Label>
              <Tabs
                value={newRecordType}
                onValueChange={(v) => setNewRecordType(v as ProductDevRecordType)}
              >
                {/* Tasks can no longer be created — only new-product and change
                    records are creatable. Existing tasks still open + edit. */}
                <TabsList className="h-8 grid grid-cols-2 w-full">
                  {RECORD_TYPES.filter((rt) => rt !== 'task').map((rt) => (
                    <TabsTrigger key={rt} value={rt} className="h-6 text-xs">
                      {t(`recordType.${rt}`)}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newStyleName">
                {newRecordType === 'task' ? t('list.dialog.titleField') : t('list.dialog.styleName')}
              </Label>
              <Input
                id="newStyleName"
                placeholder={
                  newRecordType === 'task'
                    ? t('list.dialog.taskPlaceholder')
                    : t('list.dialog.styleNamePlaceholder')
                }
                value={newStyleName}
                onChange={(e) => setNewStyleName(e.target.value)}
              />
            </div>
            {newRecordType === 'task' ? (
              <div className="space-y-2">
                <Label htmlFor="newSupplier">{t('list.dialog.supplierOptional')}</Label>
                <Select value={newSupplierId} onValueChange={setNewSupplierId}>
                  <SelectTrigger id="newSupplier"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">{t('list.dialog.noSupplier')}</SelectItem>
                    {suppliers.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="newType">{t('list.dialog.productType')}</Label>
                <Select value={newType} onValueChange={(v) => setNewType(v as ProductType)}>
                  <SelectTrigger id="newType"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRODUCT_TYPES.map((pt) => (
                      <SelectItem key={pt} value={pt}>{t(`productType.${pt}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>{t('list.dialog.cancel')}</Button>
            <Button onClick={handleCreate} disabled={!newStyleName.trim() || create.isPending}>
              {create.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('list.dialog.create')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BackToTopPill />
    </div>
  );
}
